import { useEffect, useState } from "react";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import { useAppStore } from "../../shared/store/app";
import { useT } from "../../shared/i18n";
import type { AuthConfig } from "../../shared/api/requests";
import { requests } from "../../shared/api";
import { goToSsoLogin, isLocalAuthDisabled } from "./navigation";

function oauth2ErrorMessage(code: string | null): string | null {
  if (!code) return null;
  if (code.startsWith("oauth2")) return code;
  return null;
}

export function Login() {
  const t = useT();
  const login = useAppStore((s) => s.login);
  const authError = useAppStore((s) => s.authError);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [authConfig, setAuthConfig] = useState<AuthConfig | null>(null);

  useEffect(() => {
    let cancelled = false;
    void requests.session.authConfig().then((config) => {
      if (!cancelled) setAuthConfig(config);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const params = new URLSearchParams(window.location.search);
  const oauth2Error = oauth2ErrorMessage(params.get("error"));
  const returnTo = params.get("returnTo") ?? "/";
  const ssoHref =
    returnTo.startsWith("/") && !returnTo.startsWith("//")
      ? `/api/auth/oauth2/login?returnTo=${encodeURIComponent(returnTo)}`
      : "/api/auth/oauth2/login?returnTo=%2F";

  const showSsoButton = authConfig?.oauth2.enabled === true;
  const providerName = authConfig?.oauth2.providerName ?? "SSO";
  const ssoLabel = t("loginWithProvider", { provider: providerName });
  // Kill-switch (.scratch/oauth2-ldap-auth/03): hide the password form and
  // offer only the external path(s).
  const localDisabled = isLocalAuthDisabled(authConfig);
  const isRedirectMode = authConfig?.oauth2.mode === "redirect" && showSsoButton;

  // Redirect mode (.scratch/oauth2-ldap-auth/03): anonymous browser
  // navigation goes straight to the provider. Machine API callers never run
  // this view, so POST /api/auth/login stays available to them. An error
  // code means the provider just sent the user back — stay put so the
  // explainable error remains visible instead of looping.
  useEffect(() => {
    if (isRedirectMode && !oauth2Error) {
      goToSsoLogin(ssoHref);
    }
  }, [isRedirectMode, oauth2Error, ssoHref]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    await login(username, password);
    setBusy(false);
  };

  return (
    <Box component="form" onSubmit={submit} sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 4 }}>
      <Typography variant="h6">{t("login")}</Typography>
      {oauth2Error && (
        <Typography color="error" role="alert">
          {t("oauth2Failed")} ({oauth2Error})
        </Typography>
      )}
      {localDisabled ? (
        <Typography color="text.secondary">{t("localAuthDisabled")}</Typography>
      ) : (
        <>
          <TextField
            label={t("username")}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoFocus
          />
          <TextField
            type="password"
            label={t("password")}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {authError && (
            <Typography color="error" role="alert">
              {t("loginFailed")}
            </Typography>
          )}
          <Button type="submit" variant="contained" disabled={busy || !username || !password}>
            {t("login")}
          </Button>
        </>
      )}
      {showSsoButton && (
        <>
          {!localDisabled && <Divider>{t("or")}</Divider>}
          <Button component="a" href={ssoHref} variant="outlined" aria-label={ssoLabel}>
            {ssoLabel}
          </Button>
        </>
      )}
    </Box>
  );
}
